// ============================================
// LoadSimPanel
// Load simulation configuration and results
// Validate platform survival under traffic
// Wired to real loadSimEngine + Firestore persistence
// ============================================

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Zap,
  Play,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  History,
} from 'lucide-react';

import type {
  LoadSimConfig,
  LoadSimRun,
  PersonaMixConfig,
} from '../../../types/patientJourney';

import {
  LOAD_TIERS,
  getDefaultLoadConfig,
  estimateLoadCost,
  validateLoadConfig,
  simulateLoadRun,
} from '../../../services/patientJourney/loadSimEngine';

import {
  saveLoadRun,
  getLoadRuns,
} from '../../../services/firestore/pjeService';

// ── Types ──

type TierKey = LoadSimConfig['tier'];
type Tooling = LoadSimConfig['tooling'];

const PERSONA_LABELS: { key: keyof PersonaMixConfig; label: string }[] = [
  { key: 'cleanGate1', label: 'Clean Gate 1' },
  { key: 'drugInteraction', label: 'Drug Interaction' },
  { key: 'gate2Escalation', label: 'Gate 2 Escalation' },
  { key: 'stateRestriction', label: 'State Restriction' },
  { key: 'multiProtocol', label: 'Multi-Protocol' },
  { key: 'exclusionCriteria', label: 'Exclusion Criteria' },
  { key: 'skeptic', label: 'Skeptic' },
  { key: 'overOptimizer', label: 'Over-Optimizer' },
  { key: 'custom', label: 'Custom' },
];

const TIER_KEYS: TierKey[] = ['baseline', 'growth', 'surge', 'viral', 'stress'];

// ── Helpers ──

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatMs(ms: number): string {
  if (ms >= 1000) return `${(ms / 1000).toFixed(1)}s`;
  return `${ms}ms`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ── Component ──

export function LoadSimPanel() {
  // --- State ---
  const [activeTier, setActiveTier] = useState<TierKey>('baseline');
  const [concurrentUsers, setConcurrentUsers] = useState(25);
  const [rampUp, setRampUp] = useState(30);
  const [sustain, setSustain] = useState(300);
  const [rampDown, setRampDown] = useState(30);
  const [tooling, setTooling] = useState<Tooling>('artillery');
  const [personaMix, setPersonaMix] = useState<PersonaMixConfig>(
    getDefaultLoadConfig('baseline').personaMix,
  );

  // Simulation state
  const [simRunning, setSimRunning] = useState(false);
  const [simProgress, setSimProgress] = useState(0);
  const [currentRun, setCurrentRun] = useState<LoadSimRun | null>(null);

  // Past runs
  const [pastRuns, setPastRuns] = useState<LoadSimRun[]>([]);
  const [loadingPastRuns, setLoadingPastRuns] = useState(true);

  // --- Build LoadSimConfig from UI state ---
  const buildConfig = useCallback((): LoadSimConfig => {
    const defaultConfig = getDefaultLoadConfig(activeTier);
    return {
      ...defaultConfig,
      concurrentUsers,
      rampUpSeconds: rampUp,
      sustainSeconds: sustain,
      rampDownSeconds: rampDown,
      tooling,
      personaMix,
    };
  }, [activeTier, concurrentUsers, rampUp, sustain, rampDown, tooling, personaMix]);

  const config = useMemo(() => buildConfig(), [buildConfig]);

  // --- Validation ---
  const validationErrors = useMemo(() => validateLoadConfig(config), [config]);

  // --- Cost estimate ---
  const costEstimate = useMemo(() => estimateLoadCost(config), [config]);

  // --- Persona mix total ---
  const personaTotal = Object.values(personaMix).reduce((a, b) => a + b, 0);
  const personaTotalValid = Math.abs(personaTotal - 100) < 0.01;

  // --- Load tier defaults when tier changes ---
  function selectTier(tier: TierKey) {
    setActiveTier(tier);
    const defaults = getDefaultLoadConfig(tier);
    setConcurrentUsers(defaults.concurrentUsers);
    setRampUp(defaults.rampUpSeconds);
    setSustain(defaults.sustainSeconds);
    setRampDown(defaults.rampDownSeconds);
    setTooling(defaults.tooling);
    setPersonaMix({ ...defaults.personaMix });
    setCurrentRun(null);
  }

  function updatePersonaMix(key: keyof PersonaMixConfig, value: number) {
    setPersonaMix((prev) => ({ ...prev, [key]: Math.max(0, Math.min(100, value)) }));
  }

  // --- Load past runs on mount ---
  useEffect(() => {
    let cancelled = false;
    async function loadPastRuns() {
      try {
        const runs = await getLoadRuns(10);
        if (!cancelled) setPastRuns(runs);
      } catch (err) {
        console.error('Failed to load past runs:', err);
      } finally {
        if (!cancelled) setLoadingPastRuns(false);
      }
    }
    loadPastRuns();
    return () => { cancelled = true; };
  }, []);

  // --- Simulate run ---
  async function handleSimulateRun() {
    if (simRunning) return;
    if (validationErrors.length > 0) return;

    setSimRunning(true);
    setSimProgress(0);
    setCurrentRun(null);

    // Fake progress over 3-5 seconds
    const totalMs = 3000 + Math.random() * 2000;
    const intervalMs = 100;
    let elapsed = 0;

    const interval = setInterval(() => {
      elapsed += intervalMs;
      const pct = Math.min(95, Math.round((elapsed / totalMs) * 100));
      setSimProgress(pct);
    }, intervalMs);

    // Wait for the simulation duration
    await new Promise((resolve) => setTimeout(resolve, totalMs));

    clearInterval(interval);
    setSimProgress(100);

    // Generate results
    const finalConfig = buildConfig();
    const run = simulateLoadRun(finalConfig);

    // Save to Firestore
    try {
      await saveLoadRun(run);
    } catch (err) {
      console.error('Failed to save load run:', err);
    }

    setCurrentRun(run);
    setSimRunning(false);

    // Refresh past runs
    try {
      const runs = await getLoadRuns(10);
      setPastRuns(runs);
    } catch (err) {
      console.error('Failed to refresh past runs:', err);
    }
  }

  // --- Color helpers for stat values ---
  function errorRateColor(rate: number): string {
    if (rate < 1) return 'text-emerald-400';
    if (rate < 5) return 'text-amber-400';
    return 'text-critical';
  }

  function p95Color(ms: number): string {
    if (ms < 3000) return 'text-emerald-400';
    if (ms < 8000) return 'text-amber-400';
    return 'text-critical';
  }

  function leaksColor(leaks: number): string {
    return leaks === 0 ? 'text-emerald-400' : 'text-critical';
  }

  // --- Render ---
  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div>
        <h3 className="text-lg font-display text-gold flex items-center gap-2">
          <Zap size={20} />
          Load Simulation
        </h3>
        <p className="text-base text-text-secondary mt-1">
          Validate platform survival under traffic
        </p>
      </div>

      {/* ── Tier Selector ── */}
      <div className="grid grid-cols-5 gap-3">
        {TIER_KEYS.map((tierKey) => {
          const tierDef = LOAD_TIERS[tierKey];
          return (
            <button
              key={tierKey}
              onClick={() => selectTier(tierKey)}
              className={`relative rounded-xl border-2 px-4 py-4 text-center transition-all duration-200 cursor-pointer ${
                activeTier === tierKey
                  ? 'border-gold bg-gold/10'
                  : 'border-bg-tertiary bg-bg-card hover:border-gold/30'
              }`}
            >
              <div
                className={`text-base font-semibold ${
                  activeTier === tierKey ? 'text-gold' : 'text-text-primary'
                }`}
              >
                {tierDef.label}
              </div>
              <div className="text-sm text-text-muted mt-0.5">
                {tierDef.minUsers.toLocaleString()}-{tierDef.maxUsers.toLocaleString()} users
              </div>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── Left Column ── */}
        <div className="space-y-6">
          {/* Configuration */}
          <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
              <Clock size={16} className="text-gold" />
              <span className="text-base font-semibold font-display text-gold">
                Configuration
              </span>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-base text-text-secondary mb-1.5">
                  Concurrent Users
                </label>
                <input
                  type="number"
                  min={1}
                  value={concurrentUsers}
                  onChange={(e) => setConcurrentUsers(Number(e.target.value))}
                  className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors"
                />
                <p className="text-sm text-text-muted mt-1">
                  Tier range: {LOAD_TIERS[activeTier].minUsers.toLocaleString()}-{LOAD_TIERS[activeTier].maxUsers.toLocaleString()}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-base text-text-secondary mb-1.5">
                    Ramp Up (s)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={rampUp}
                    onChange={(e) => setRampUp(Number(e.target.value))}
                    className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-base text-text-secondary mb-1.5">
                    Sustain (s)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={sustain}
                    onChange={(e) => setSustain(Number(e.target.value))}
                    className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-base text-text-secondary mb-1.5">
                    Ramp Down (s)
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={rampDown}
                    onChange={(e) => setRampDown(Number(e.target.value))}
                    className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-base text-text-secondary mb-1.5">
                  Tooling
                </label>
                <select
                  value={tooling}
                  onChange={(e) => setTooling(e.target.value as Tooling)}
                  className="w-full bg-bg-elevated border border-bg-tertiary rounded-lg px-3 py-2.5 text-base text-text-primary focus:outline-none focus:border-gold/50 transition-colors appearance-none cursor-pointer"
                >
                  <option value="k6">k6</option>
                  <option value="artillery">Artillery</option>
                  <option value="custom_cloud_function">Custom Cloud Function</option>
                </select>
              </div>
            </div>
          </div>

          {/* Persona Mix */}
          <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Users size={16} className="text-purple-400" />
                <span className="text-base font-semibold font-display text-gold">
                  Persona Mix
                </span>
              </div>
              <span
                className={`text-base font-semibold px-2.5 py-0.5 rounded-lg ${
                  personaTotalValid
                    ? 'text-emerald-400 bg-emerald-400/10'
                    : 'text-critical bg-critical/10'
                }`}
              >
                Total: {personaTotal}%
              </span>
            </div>

            <div className="px-5 py-4 space-y-3">
              {PERSONA_LABELS.map(({ key, label }) => (
                <div key={key} className="flex items-center justify-between gap-3">
                  <span className="text-base text-text-secondary flex-1">{label}</span>
                  <div className="flex items-center gap-1.5">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={personaMix[key]}
                      onChange={(e) => updatePersonaMix(key, Number(e.target.value))}
                      className="w-20 bg-bg-elevated border border-bg-tertiary rounded-lg px-2 py-2 text-base text-text-primary text-center focus:outline-none focus:border-gold/50 transition-colors"
                    />
                    <span className="text-base text-text-muted">%</span>
                  </div>
                </div>
              ))}

              {!personaTotalValid && (
                <div className="flex items-center gap-2 text-critical text-base mt-2">
                  <AlertTriangle size={16} />
                  Persona mix must total 100% (currently {personaTotal}%)
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Right Column ── */}
        <div className="space-y-6">
          {/* Cost Estimate (dynamic) */}
          <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
              <DollarSign size={16} className="text-emerald-400" />
              <span className="text-base font-semibold font-display text-gold">
                Cost Estimate
              </span>
            </div>

            <div className="px-5 py-4 space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-bg-tertiary/50">
                <span className="text-base text-text-secondary">MedGemma Inference</span>
                <span className="text-base text-text-primary font-semibold">
                  {formatCurrency(costEstimate.medgemmaInferenceCost)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-bg-tertiary/50">
                <span className="text-base text-text-secondary">Firestore Read/Write</span>
                <span className="text-base text-text-primary font-semibold">
                  {formatCurrency(costEstimate.firestoreReadWriteCost)}
                </span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-bg-tertiary/50">
                <span className="text-base text-text-secondary">Cloud Run Compute</span>
                <span className="text-base text-text-primary font-semibold">
                  {formatCurrency(costEstimate.cloudRunComputeCost)}
                </span>
              </div>
              <div className="flex items-center justify-between py-3 bg-gold/5 rounded-lg px-3 -mx-1">
                <span className="text-base text-gold font-semibold">Total Monthly Estimate</span>
                <span className="text-lg text-gold font-bold">
                  {formatCurrency(costEstimate.totalEstimatedMonthlyCost)}
                </span>
              </div>
              <p className="text-sm text-text-muted">
                Based on {concurrentUsers.toLocaleString()} concurrent users, {rampUp + sustain + rampDown}s total duration, projected to 30 days
              </p>
            </div>
          </div>

          {/* Validation Errors + Run Controls */}
          <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
              <Play size={16} className="text-gold" />
              <span className="text-base font-semibold font-display text-gold">
                Run Controls
              </span>
            </div>

            <div className="px-5 py-4 space-y-4">
              {/* Validation errors */}
              {validationErrors.length > 0 && (
                <div className="bg-critical/5 border border-critical/20 rounded-lg px-4 py-3 space-y-1.5">
                  <div className="flex items-center gap-2 text-critical text-base font-semibold mb-1">
                    <AlertTriangle size={16} />
                    Configuration Issues
                  </div>
                  <ul className="space-y-1">
                    {validationErrors.map((err, i) => (
                      <li key={i} className="text-base text-critical/80 flex items-start gap-2">
                        <XCircle size={14} className="mt-0.5 shrink-0" />
                        <span>{err}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Progress bar */}
              {simRunning && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-base text-gold">
                    <Loader2 size={16} className="animate-spin" />
                    Simulating load test... {simProgress}%
                  </div>
                  <div className="w-full h-2 bg-bg-elevated rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gold rounded-full transition-all duration-100"
                      // Using Tailwind-compatible approach: width set via inline for dynamic %
                      // This is the one exception — dynamic percentage width requires it
                      style={{ width: `${simProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Run button */}
              <button
                onClick={handleSimulateRun}
                disabled={simRunning || validationErrors.length > 0}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-base transition-all duration-200 ${
                  simRunning || validationErrors.length > 0
                    ? 'bg-gold/20 text-gold cursor-not-allowed opacity-60'
                    : 'bg-gold text-bg-primary hover:bg-gold/90 cursor-pointer'
                }`}
              >
                {simRunning ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Running Simulation...
                  </>
                ) : (
                  <>
                    <Play size={16} />
                    Simulate Load Run
                  </>
                )}
              </button>

              {validationErrors.length > 0 && !simRunning && (
                <p className="text-sm text-text-muted text-center">
                  Fix configuration issues above to enable simulation
                </p>
              )}
            </div>
          </div>

          {/* Results Dashboard */}
          <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
              <TrendingUp size={16} className="text-blue-400" />
              <span className="text-base font-semibold font-display text-gold">
                Results Dashboard
              </span>
            </div>

            <div className="px-5 py-4">
              {currentRun ? (
                <div className="space-y-4">
                  {/* Status badge */}
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-emerald-400" />
                    <span className="text-base text-emerald-400 font-semibold">
                      Simulation Complete
                    </span>
                    <span className="text-sm text-text-muted ml-auto">
                      {formatDate(currentRun.startedAt)}
                    </span>
                  </div>

                  {/* 4 stat cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard
                      label="Peak Concurrency"
                      value={currentRun.results.peakConcurrency.toLocaleString()}
                      icon={Users}
                      valueColor="text-text-primary"
                    />
                    <StatCard
                      label="Error Rate"
                      value={`${currentRun.results.errorRate}%`}
                      icon={AlertTriangle}
                      threshold="< 1%"
                      valueColor={errorRateColor(currentRun.results.errorRate)}
                    />
                    <StatCard
                      label="p95 Latency"
                      value={formatMs(currentRun.results.latency.p95)}
                      icon={Clock}
                      threshold="< 3s"
                      valueColor={p95Color(currentRun.results.latency.p95)}
                    />
                    <StatCard
                      label="Session Leaks"
                      value={currentRun.results.sessionLeaks.toString()}
                      icon={Zap}
                      threshold="= 0"
                      valueColor={leaksColor(currentRun.results.sessionLeaks)}
                    />
                  </div>

                  {/* Extended metrics */}
                  <div className="bg-bg-elevated rounded-lg px-4 py-3 space-y-2">
                    <p className="text-sm font-semibold text-text-secondary mb-2">Extended Metrics</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      <div className="flex justify-between">
                        <span className="text-base text-text-muted">p50 Latency</span>
                        <span className="text-base text-text-primary font-medium">
                          {formatMs(currentRun.results.latency.p50)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base text-text-muted">p99 Latency</span>
                        <span className="text-base text-text-primary font-medium">
                          {formatMs(currentRun.results.latency.p99)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base text-text-muted">Total Requests</span>
                        <span className="text-base text-text-primary font-medium">
                          {currentRun.results.totalRequests.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base text-text-muted">Total Errors</span>
                        <span className="text-base text-text-primary font-medium">
                          {currentRun.results.totalErrors.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-base text-text-muted">Protocol Accuracy</span>
                        <span className="text-base text-text-primary font-medium">
                          {currentRun.results.protocolAccuracyRate}%
                        </span>
                      </div>
                      {currentRun.results.breakingPoint !== undefined && (
                        <div className="flex justify-between">
                          <span className="text-base text-text-muted">Breaking Point</span>
                          <span className="text-base text-critical font-medium">
                            {currentRun.results.breakingPoint.toLocaleString()} users
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Run cost breakdown */}
                  <div className="bg-bg-elevated rounded-lg px-4 py-3">
                    <p className="text-sm font-semibold text-text-secondary mb-2">Run Cost</p>
                    <div className="flex items-center justify-between">
                      <span className="text-base text-text-muted">Single Run</span>
                      <span className="text-base text-text-primary font-medium">
                        {formatCurrency(
                          currentRun.results.costEstimate.medgemmaInferenceCost +
                          currentRun.results.costEstimate.firestoreReadWriteCost +
                          currentRun.results.costEstimate.cloudRunComputeCost
                        )}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-base text-text-muted">Monthly Projection</span>
                      <span className="text-base text-gold font-semibold">
                        {formatCurrency(currentRun.results.costEstimate.totalEstimatedMonthlyCost)}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard label="Peak Concurrency" value="--" icon={Users} valueColor="text-text-muted" />
                    <StatCard label="Error Rate" value="--" icon={AlertTriangle} threshold="< 1%" valueColor="text-text-muted" />
                    <StatCard label="p95 Latency" value="--" icon={Clock} threshold="< 3s" valueColor="text-text-muted" />
                    <StatCard label="Session Leaks" value="--" icon={Zap} threshold="= 0" valueColor="text-text-muted" />
                  </div>

                  <p className="text-base text-text-muted mt-4 text-center">
                    Run simulation to see results
                  </p>

                  {/* Alert thresholds */}
                  <div className="mt-4 pt-4 border-t border-bg-tertiary">
                    <p className="text-sm text-text-muted mb-2">Alert thresholds:</p>
                    <div className="flex flex-wrap gap-2">
                      <span className="text-sm px-2.5 py-1 rounded-lg bg-bg-elevated border border-bg-tertiary text-text-muted">
                        p95 &lt; 3s
                      </span>
                      <span className="text-sm px-2.5 py-1 rounded-lg bg-bg-elevated border border-bg-tertiary text-text-muted">
                        Error rate &lt; 1%
                      </span>
                      <span className="text-sm px-2.5 py-1 rounded-lg bg-bg-elevated border border-bg-tertiary text-text-muted">
                        Session leaks = 0
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Past Runs ── */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-bg-tertiary bg-bg-elevated/50 flex items-center gap-2">
          <History size={16} className="text-blue-400" />
          <span className="text-base font-semibold font-display text-gold">
            Past Runs
          </span>
          <span className="text-sm text-text-muted ml-auto">
            {pastRuns.length} run{pastRuns.length !== 1 ? 's' : ''}
          </span>
        </div>

        <div className="px-5 py-4">
          {loadingPastRuns ? (
            <div className="flex items-center justify-center gap-2 py-6 text-text-muted">
              <Loader2 size={16} className="animate-spin" />
              <span className="text-base">Loading past runs...</span>
            </div>
          ) : pastRuns.length === 0 ? (
            <p className="text-base text-text-muted text-center py-6">
              No past runs yet. Run a simulation to see history.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-base">
                <thead>
                  <tr className="border-b border-bg-tertiary">
                    <th className="text-left py-2 pr-3 text-text-muted font-medium text-sm">
                      Tier
                    </th>
                    <th className="text-right py-2 px-3 text-text-muted font-medium text-sm">
                      Users
                    </th>
                    <th className="text-right py-2 px-3 text-text-muted font-medium text-sm">
                      Date
                    </th>
                    <th className="text-right py-2 px-3 text-text-muted font-medium text-sm">
                      Protocol Accuracy
                    </th>
                    <th className="text-right py-2 px-3 text-text-muted font-medium text-sm">
                      p95 Latency
                    </th>
                    <th className="text-right py-2 pl-3 text-text-muted font-medium text-sm">
                      Error Rate
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pastRuns.map((run) => (
                    <tr
                      key={run.runId}
                      className="border-b border-bg-tertiary/50 last:border-0 hover:bg-bg-elevated/30 transition-colors duration-150"
                    >
                      <td className="py-2.5 pr-3">
                        <span className="text-base text-text-primary font-semibold capitalize">
                          {run.config.tier}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">
                        {run.config.concurrentUsers.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3 text-right text-text-secondary">
                        {formatDate(run.startedAt)}
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <span className={`font-medium ${
                          run.results.protocolAccuracyRate >= 95
                            ? 'text-emerald-400'
                            : run.results.protocolAccuracyRate >= 85
                            ? 'text-amber-400'
                            : 'text-critical'
                        }`}>
                          {run.results.protocolAccuracyRate}%
                        </span>
                      </td>
                      <td className={`py-2.5 px-3 text-right font-medium ${p95Color(run.results.latency.p95)}`}>
                        {formatMs(run.results.latency.p95)}
                      </td>
                      <td className={`py-2.5 pl-3 text-right font-medium ${errorRateColor(run.results.errorRate)}`}>
                        {run.results.errorRate}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Sub-components ──

function StatCard({
  label,
  value,
  icon: Icon,
  threshold,
  valueColor,
}: {
  label: string;
  value: string;
  icon: typeof Users;
  threshold?: string;
  valueColor: string;
}) {
  return (
    <div className="bg-bg-elevated border border-bg-tertiary rounded-xl px-4 py-4 text-center">
      <Icon size={20} className="text-text-muted/40 mx-auto mb-2" />
      <div className={`text-2xl font-bold ${valueColor}`}>{value}</div>
      <div className="text-sm text-text-muted mt-1">{label}</div>
      {threshold && (
        <div className="text-xs text-text-muted mt-1 opacity-60">
          Target: {threshold}
        </div>
      )}
    </div>
  );
}
